import React, { useId, isValidElement, cloneElement } from "react";

export const Label = ({ className = "", children, ...props }) => (
  <label
    className={`mb-2 block text-[13px] font-semibold tracking-wide text-ink-600 dark:text-ink-300 ${className}`}
    {...props}
  >
    {children}
  </label>
);

const Input = React.forwardRef(({ className = "", error, ...props }, ref) => (
  <input
    ref={ref}
    className={`h-12 w-full rounded-[18px] border-[0.5px] bg-white dark:bg-ink-900 px-4 text-[15px] font-medium text-ink-900 dark:text-ink-100
      placeholder:text-ink-300 dark:placeholder:text-ink-500 placeholder:font-normal
      focus:outline-none focus:ring-[3px] focus:ring-brand-500/15 focus:border-brand-500 transition-all duration-200
      ${error
        ? "border-red-400 bg-red-50/30 dark:bg-red-950/30 focus:ring-red-400/15 focus:border-red-400"
        : "border-ink-200 dark:border-ink-700 hover:border-ink-300 dark:hover:border-ink-600 focus:bg-white dark:focus:bg-ink-900"} ${className}`}
    {...props}
  />
));


// Field wires the visible <Label> to its control via htmlFor/id so screen
// readers and "click label to focus" both work. It auto-generates a stable
// id with useId() (or uses an explicit `id`/`name` prop when provided), then
// injects that id onto its child control if the child doesn't already
// declare its own `id`. This is safe even when the child is a wrapper <div>
// (e.g. a password field with a show/hide button) — it just adds an unused
// id attribute in that case, it never removes or overrides an existing id.
export const Field = ({ label, error, children, className = "", id, name }) => {
  const generatedId = useId();
  const fieldId = id || name || generatedId;

  const control =
    isValidElement(children) && children.props.id === undefined
      ? cloneElement(children, { id: fieldId })
      : children;

  return (
    <div className={className}>
      {label && <Label htmlFor={fieldId}>{label}</Label>}
      {control}
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
};

export default Input;
